import React, { useState } from 'react';
import { savePurchase, deletePurchase } from '../../data/purchases';
import { printBonDeCommande, getCompanyInfo } from '../../data/supplierDocuments';
import { updateProductPrices } from '../../data/catalog';

export default function PurchaseOrderList({
  purchases, setPurchases, suppliers, catalogProducts, setCatalogProducts,
  purchaseStats, search, setSearch, message, setMessage,
  onEditPurchase, onReceivePurchase, onCreateInvoice
}) {
  const [expandedRow, setExpandedRow] = useState(null);
  const [editablePrices, setEditablePrices] = useState({});

  const formatMoney = (val) => Number(val).toFixed(2) + ' €';

  const handleDelete = (id) => {
    if (!window.confirm('Supprimer cette commande ?')) return;
    deletePurchase(id);
    setPurchases(purchases.filter(p => p.id !== id));
    setMessage('Commande supprimée.');
    setTimeout(() => setMessage(''), 3000);
  };

  const handlePrint = (purchase) => {
    const supplier = suppliers.find(s => s.id === purchase.supplierId);
    printBonDeCommande(purchase, supplier);
  };

  const handleEmail = (purchase) => {
    const supplier = suppliers.find(s => s.id === purchase.supplierId);
    if (!supplier || !supplier.email) {
      alert("Le fournisseur n'a pas d'adresse email configurée.");
      return;
    }
    const subject = encodeURIComponent(`Bon de Commande ${purchase.orderNumber || purchase.id}`);
    const body = encodeURIComponent(`Bonjour,\n\nVeuillez trouver ci-joint notre bon de commande ${purchase.orderNumber || purchase.id}.\n\nCordialement,`);
    window.location.href = `mailto:${supplier.email}?subject=${subject}&body=${body}`;
  };

  const handleToggleExpand = (purchase) => {
    if (expandedRow === purchase.id) {
      setExpandedRow(null);
    } else {
      setExpandedRow(purchase.id);
      // Initialize editable prices state for this purchase
      const initialPrices = {};
      purchase.items.forEach(item => {
        const prod = catalogProducts.find(p => p.id === item.productId);
        initialPrices[item.productId] = {
          unitCost: item.unitCost || 0,
          
        };
      });
      setEditablePrices(initialPrices);
    }
  };

  const handlePriceChange = (productId, field, value) => {
    setEditablePrices(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: Number(value)
      }
    }));
  };

  const handleSavePrices = (purchase) => {
    // 1. Update purchase items' unitCost
    const updatedItems = purchase.items.map(item => {
      const edited = editablePrices[item.productId];
      if (edited) {
        return { ...item, unitCost: edited.unitCost };
      }
      return item;
    });
    
    // Recalculate total
    const totalAmount = updatedItems.reduce((sum, item) => sum + (item.quantity * item.unitCost), 0);
    const updatedPurchase = { ...purchase, items: updatedItems, totalAmount };
    
    savePurchase(updatedPurchase);
    setPurchases(purchases.map(p => p.id === purchase.id ? updatedPurchase : p));

    // 2. Update catalog products
    Object.keys(editablePrices).forEach(productId => {
      const { unitCost, salePrice } = editablePrices[productId];
      updateProductPrices(productId, { purchasePrice: unitCost, price: salePrice });
    });

    setMessage('Prix mis à jour avec succès.');
    setTimeout(() => setMessage(''), 3000);
  };

  const filteredPurchases = purchases.filter(p => {
    const s = suppliers.find(sup => sup.id === p.supplierId);
    const sName = s ? s.name.toLowerCase() : '';
    const term = search.toLowerCase();
    return sName.includes(term) || (p.orderNumber && p.orderNumber.toLowerCase().includes(term));
  }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft': return <span className="badge bg-secondary">Brouillon</span>;
      case 'ordered': return <span className="badge bg-primary">Commandée</span>;
      case 'partial': return <span className="badge bg-warning text-dark">Partielle</span>;
      case 'received': return <span className="badge bg-success">Reçue</span>;
      case 'cancelled': return <span className="badge bg-danger">Annulée</span>;
      default: return <span className="badge bg-secondary">{status}</span>;
    }
  };

  return (
    <div>
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card text-white bg-secondary mb-3">
            <div className="card-body">
              <h5 className="card-title">Brouillons</h5>
              <h3>{purchaseStats.draft || 0}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-white bg-primary mb-3">
            <div className="card-body">
              <h5 className="card-title">Commandées</h5>
              <h3>{purchaseStats.ordered || 0}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-dark bg-warning mb-3">
            <div className="card-body">
              <h5 className="card-title">Réception Partielle</h5>
              <h3>{purchaseStats.partial || 0}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-white bg-success mb-3">
            <div className="card-body">
              <h5 className="card-title">Reçues</h5>
              <h3>{purchaseStats.received || 0}</h3>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-3">
        <input type="text" className="form-control" placeholder="Rechercher une commande..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th>N° Commande</th>
              <th>Date</th>
              <th>Fournisseur</th>
              <th>Articles</th>
              <th>Montant</th>
              <th>Statut</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPurchases.map(p => {
              const supplier = suppliers.find(s => s.id === p.supplierId);
              const isExpanded = expandedRow === p.id;
              
              return (
                <React.Fragment key={p.id}>
                  <tr style={{ cursor: 'pointer' }} onClick={() => handleToggleExpand(p)}>
                    <td><strong>{p.orderNumber || p.id.substring(0,8)}</strong></td>
                    <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                    <td>{supplier ? supplier.name : 'Inconnu'}</td>
                    <td>{p.items.length}</td>
                    <td>{formatMoney(p.totalAmount)}</td>
                    <td>{getStatusBadge(p.status)}</td>
                    <td className="text-end" onClick={e => e.stopPropagation()}>
                      {['draft', 'ordered', 'partial'].includes(p.status) && (
                        <button className="btn btn-sm btn-success me-1" onClick={() => onReceivePurchase(p)}>Recevoir</button>
                      )}
                      <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => handlePrint(p)}>📄</button>
                      <button className="btn btn-sm btn-outline-info me-1" onClick={() => handleEmail(p)}>📧</button>
                      {p.status === 'received' && (
                        <button className="btn btn-sm btn-outline-primary me-1" onClick={() => onCreateInvoice(p)}>📝 Facture</button>
                      )}
                      <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => onEditPurchase(p)}>✏️</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(p.id)}>🗑️</button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-light">
                      <td colSpan="7">
                        <div className="p-3 border rounded bg-white">
                          <h6>Détails de la commande et Prix</h6>
                          <table className="table table-sm table-bordered mt-2">
                            <thead className="table-secondary">
                              <tr>
                                <th>Produit</th>
                                <th>Qté</th>
                                <th>Coût Unitaire HT</th>
                                <th>Total Ligne</th>
                              </tr>
                            </thead>
                            <tbody>
                              {p.items.map(item => {
                                const prod = catalogProducts.find(cp => cp.id === item.productId);
                                const currentPrices = editablePrices[item.productId] || { unitCost: item.unitCost };
                                
                                return (
                                  <tr key={item.productId}>
                                    <td>{prod ? prod.name : 'Produit inconnu'}</td>
                                    <td>{item.quantity} {item.receivedQuantity !== undefined ? `(Reçu: ${item.receivedQuantity})` : ''}</td>
                                    <td>
                                      <input type="number" step="0.01" className="form-control form-control-sm" 
                                        value={currentPrices.unitCost} 
                                        onChange={(e) => handlePriceChange(item.productId, 'unitCost', e.target.value)} 
                                        style={{ width: '100px' }} />
                                    </td>
                                    <td>{formatMoney((item.quantity || 0) * (currentPrices.unitCost || 0))}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          <div className="text-end">
                            <button className="btn btn-sm btn-primary" onClick={() => handleSavePrices(p)}>
                              Enregistrer les prix
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {filteredPurchases.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center text-muted py-4">Aucune commande trouvée.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

